import { useState, useEffect, useCallback } from 'react';
import { useLazyQuery, useQuery } from 'data-fetching-client';
import { sourceDataMutate } from 'automation-ui-formula-editor/data-sources/utils/mutateDataSourceUtils';
import { useDataSourcesContext } from 'automation-ui-formula-editor/data-sources/DataSourcesContext';
import { GET_DATA_SOURCES_BY_PREFIX } from 'automation-ui-formula-editor/data-sources/queries/getPrefixQueries';
import useResolveObjectTypeByIdPromise from 'automation-ui-formula-editor/data-sources/hooks/useResolveObjectTypeByIdPromise';
import useResolvePropertyOptionLabelPromise from 'automation-ui-formula-editor/data-sources/hooks/useResolvePropertyOptionLabelPromise';
export const useGetDataSources = prefixReqs => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);

  // pull in our data dictionary to get the mutators we need
  const {
    flowId,
    flowObjectTypeId,
    getActionHeaderTitle
  } = useDataSourcesContext();
  const resolveObjectTypeById = useResolveObjectTypeByIdPromise();
  const resolvePropertyOptionLabel = useResolvePropertyOptionLabelPromise();
  const {
    data: queryData,
    loading: queryLoading,
    error
  } = useQuery(GET_DATA_SOURCES_BY_PREFIX, {
    skip: !prefixReqs || !prefixReqs.length,
    variables: {
      flowId,
      flowObjectTypeId,
      prefixReqs
    }
  });
  const dataSourcesByPrefix = queryData === null || queryData === void 0 ? void 0 : queryData.getDataSources;
  useEffect(() => {
    if (queryLoading) {
      return;
    }
    if (error) {
      setLoading(false);
      return;
    }
    if (dataSourcesByPrefix && dataSourcesByPrefix.size > 0) {
      Promise.allSettled(Array.from(dataSourcesByPrefix.keys()).map(key => {
        const value = dataSourcesByPrefix.get(key);
        if (!value || !value.dataSourceDefinition || !value.dataSourceProperties) {
          return Promise.reject(new Error('Invalid data source'));
        }
        return sourceDataMutate(value, {
          getActionHeaderTitle,
          resolveObjectTypeById,
          resolvePropertyOptionLabel
        }).then(mutatedData => {
          return {
            key,
            value: mutatedData
          };
        }).catch(() => {
          return {
            key,
            value
          };
        });
      })).then(responses => {
        const newData = responses.filter(response => response.status === 'fulfilled').map(response => {
          return response.value;
        }).reduce((acc, {
          key,
          value
        }) => {
          acc.set(key, value);
          return acc;
        }, new Map());
        setData(newData);
      }).finally(() => setLoading(false)).catch(() => setData(dataSourcesByPrefix));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData, error]);
  return {
    data,
    loading,
    error
  };
};
export const useLazyGetDataSources = () => {
  const [resolvers, setResolvers] = useState();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);

  // pull in our data dictionary to get the mutators we need
  const {
    flowId,
    flowObjectTypeId,
    getActionHeaderTitle
  } = useDataSourcesContext();
  const resolveObjectTypeById = useResolveObjectTypeByIdPromise();
  const resolvePropertyOptionLabel = useResolvePropertyOptionLabelPromise();
  const [getDataSourcesQuery, {
    called,
    data: queryData,
    loading: queryLoading,
    error
  }] = useLazyQuery(GET_DATA_SOURCES_BY_PREFIX, {
    variables: {
      flowId: null,
      flowObjectTypeId: '',
      prefixReqs: []
    }
  });
  useEffect(() => {
    if (!called || queryLoading) {
      return;
    }
    if (error) {
      setLoading(false);
      return;
    }
    if (queryData) {
      if (!queryData.getDataSources) {
        setLoading(false);
        if (resolvers && resolvers.onReject) {
          resolvers.onReject('Unable to retrieve data source');
        }
      }
      Promise.allSettled(Array.from(queryData.getDataSources.keys()).map(key => {
        const value = queryData.getDataSources.get(key);
        if (!value || !value.dataSourceDefinition || !value.dataSourceProperties) {
          return Promise.reject(new Error('Invalid data source'));
        }
        return sourceDataMutate(value, {
          getActionHeaderTitle,
          resolveObjectTypeById,
          resolvePropertyOptionLabel
        }).then(mutatedData => {
          return {
            key,
            value: mutatedData
          };
        }).catch(() => {
          return {
            key,
            value
          };
        });
      })).then(responses => {
        const newData = responses.filter(response => response.status === 'fulfilled').map(response => {
          return response.value;
        }).reduce((acc, {
          key,
          value
        }) => {
          acc.set(key, value);
          return acc;
        }, new Map());
        setData(newData);
        if (resolvers && resolvers.onResolve) {
          resolvers.onResolve(newData);
        }
      }).finally(() => setLoading(false)).catch(() => {
        setData(queryData.getDataSources);
        if (resolvers && resolvers.onReject) {
          resolvers.onReject('Source data mutated failed');
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData, queryLoading, error]);
  const getDataSources = useCallback(prefixReqs => {
    getDataSourcesQuery({
      // @ts-expect-error FIXME: update code for `TypedDocumentNode` changes
      variables: {
        flowId,
        flowObjectTypeId,
        prefixReqs
      }
    });
  }, [flowId, flowObjectTypeId, getDataSourcesQuery]);
  const getDataSourcesWithAPromise = useCallback(prefixReqs => {
    return new Promise((onResolve, onReject) => {
      setResolvers({
        onResolve,
        onReject
      });
      getDataSources(prefixReqs);
    });
  }, [getDataSources]);
  return {
    getDataSources,
    data,
    loading,
    error,
    getDataSourcesWithAPromise
  };
};